import { Shield, ShieldAlert, Crosshair, Layers } from "lucide-react";
import { SmoothNum } from "./v2/primitives";
import { useState } from "react";
import { api } from "../lib/api";

const PHASES = [["initial_stop", "STOP"], ["tp_orders", "TP"], ["break_even", "BE"], ["trailing", "TRAIL"], ["partials", "PARTIAL"], ["scale_in", "ADD"]];

export const PhaseBar = ({ cfg, prefix = "" }) => {
  const [busy, setBusy] = useState(false);
  const armed = !cfg.shadow_mode && !!cfg.phases?.initial_stop;
  const toggle = async () => {
    setBusy(true);
    try { await api.pmeConfig(armed ? { phases: { initial_stop: false } } : { shadow_mode: false, phases: { initial_stop: true } }); } catch {}
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-1 px-2 py-1 border-b border-zinc-800/60 flex-wrap" data-testid={`${prefix}pme-phase-bar`}>
      {PHASES.map(([k, t]) => { const live = !cfg.shadow_mode && !!cfg.phases?.[k]; return <span key={k} data-testid={`${prefix}pme-phase-chip-${k}`} data-live={live} className={`text-[7.5px] font-mono px-1 rounded border ${live ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10" : "border-zinc-700 text-zinc-500"}`}>{t} {live ? "LIVE" : "SHADOW"}</span>; })}
      <button onClick={toggle} disabled={busy} data-testid={`${prefix}pme-phase1-toggle`} className={`ml-auto text-[8px] font-mono px-1.5 py-0.5 rounded border ${armed ? "border-emerald-500/60 text-emerald-300" : "border-amber-500/60 text-amber-300"}`}>{armed ? "PHASE 1 ARMED" : "ARM PHASE 1"}</button>
    </div>
  );
};

/* POSITION INTELLIGENCE — deterministic Position Management Engine view (shadow-aware). */

const STATE_COL = {
  ENTERED: "text-zinc-300", RISK_ON: "text-amber-300", TP1_APPROACHING: "text-cyan-300", TP1_HIT: "text-emerald-300",
  BREAK_EVEN_PROTECTED: "text-emerald-300", SCALE_IN_ELIGIBLE: "text-fuchsia-300", SCALED_IN: "text-fuchsia-300",
  TP2_APPROACHING: "text-cyan-300", TP2_HIT: "text-emerald-300", RUNNER: "text-emerald-400", TRAILING: "text-emerald-400",
  EXITING: "text-amber-300", CLOSED: "text-zinc-500",
};
const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const rTxt = (r) => (r == null ? "—" : `${r >= 0 ? "+" : ""}${Number(r).toFixed(2)}R`);

const Tp = ({ n, tp, st, d }) => (
  <div className="flex items-center justify-between text-[9.5px] font-mono" data-testid={`pme-tp${n}`}>
    <span className="text-zinc-500">TP{n} <span className="text-zinc-300 tabular-nums">{fmt(tp, d)}</span></span>
    <span className={st.status === "HIT" ? "text-emerald-400 font-bold" : st.status === "APPROACHING" ? "text-cyan-300" : "text-zinc-400"}>
      {st.status === "HIT" ? "HIT" : <><SmoothNum value={st.progress || 0} format={(n) => `${Math.round(n)}%`} /> · {st.status === "APPROACHING" ? "APPROACHING" : "ACTIVE"}</>}
    </span>
    {st.status !== "HIT" && <span className="ml-2 h-1 w-14 overflow-hidden rounded-full bg-white/5"><span className={`block h-full rounded-full ${st.status === "APPROACHING" ? "bg-cyan-300 r2-tp-pulse" : "bg-zinc-500"}`} style={{ width: `${Math.min(100, st.progress || 0)}%`, transition: "width 600ms ease" }} /></span>}
  </div>
);

const Kv = ({ k, v, col = "text-zinc-200", testid }) => (
  <div>
    <div className="text-[8px] font-mono uppercase tracking-[0.18em] text-zinc-600">{k}</div>
    <div className={`text-[10.5px] font-mono font-semibold tabular-nums ${col}`} data-testid={testid}>{v}</div>
  </div>
);

export const PositionIntelligence = ({ pme }) => {
  const list = pme?.positions || [];
  const cfg = pme?.config || {};
  if (!list.length) {
    return (
      <div className="h-full flex flex-col" data-testid="pme-empty">
        <PhaseBar cfg={cfg} />
        <div className="flex-1 grid place-items-center text-center px-3">
          <div className="space-y-1">
            <Crosshair size={14} className="mx-auto text-zinc-600" />
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">No position to manage</div>
            <div className="text-[9px] text-zinc-600">{!cfg.shadow_mode && cfg.phases?.initial_stop ? "Initial stop auto-attaches to any unprotected live position" : "Position engine in SHADOW mode"} · TP, BE, trail, partials, scale-ins planned only</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="h-full overflow-y-auto pr-0.5 space-y-2" data-testid="pme-panel">
      <PhaseBar cfg={cfg} />
      {list.map((m) => {
        const d = Math.abs(m.averageEntry) >= 1000 ? 2 : 4;
        const prot = m.protection === "PROTECTED";
        const pmRec = m.agent?.position_management;
        const rs = m.agent?.risk_supervisor;
        return (
          <div key={m.key} className="rounded-md border border-white/[0.08] bg-white/[0.02] p-2.5 space-y-2" data-testid={`pme-pos-${m.symbol}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[11px] font-black tracking-[0.1em] ${m.side === "LONG" ? "text-emerald-400" : "text-red-400"}`} data-testid="pme-headline">
                {m.symbol} {m.side} · {m.quantity}x
              </span>
              <span className={`text-[9px] font-mono tracking-[0.14em] ${STATE_COL[m.tradeState] || "text-zinc-300"}`} data-testid="pme-state">{m.tradeState.replace(/_/g, " ")}</span>
            </div>
            <div className={`flex items-center gap-1.5 rounded px-2 py-1 text-[9px] font-mono tracking-[0.14em] ${prot ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/15 text-red-300 animate-pulse"}`} data-testid="pme-protection">
              {prot ? <Shield size={10} /> : <ShieldAlert size={10} />} {prot ? "PROTECTED" : "WARNING · POSITION UNPROTECTED"}
              <span className="ml-auto text-zinc-500">{m.shadow ? "MGMT: SHADOW" : "MGMT: PHASE 1"}</span>
            </div>
            <div className="grid grid-cols-3 gap-x-2 gap-y-1.5">
              <Kv k="Avg entry" v={fmt(m.averageEntry, d)} />
              <Kv k="Current R" v={<SmoothNum value={m.currentR} format={rTxt} />} col={m.currentR >= 0 ? "text-emerald-400" : "text-red-400"} testid="pme-current-r" />
              <Kv k="Locked R" v={<SmoothNum value={m.lockedR} format={rTxt} />} col={m.lockedR >= 0 ? "text-emerald-400" : "text-zinc-400"} testid="pme-locked-r" />
              <Kv k="Current stop" v={fmt(m.currentStop, d)} col="text-amber-300" testid="pme-current-stop" />
              <Kv k="Initial stop" v={fmt(m.initialStop, d)} col="text-zinc-400" />
              <Kv k="Open risk" v={`$${fmt(m.openRiskUsd, 0)}`} col="text-zinc-300" />
              <Kv k="Runner" v={m.runnerQty != null ? `${m.runnerQty} contract` : m.originalQty > 1 ? `${Math.max(0, m.originalQty - (cfg.partials_pct || []).reduce((a, b) => a + Math.round(m.originalQty * b / 100), 0))} planned` : "1-lot"} />
              <Kv k="Scale-in" v={m.scaleInEligible ? "ELIGIBLE" : <><SmoothNum value={m.scaleInScore} format={(n) => String(Math.round(n))} />/100</>} col={m.scaleInEligible ? "text-fuchsia-300" : "text-zinc-400"} testid="pme-scale-in" />
              <Kv k="Stop basis" v={m.stopBasis} col="text-zinc-400" />
            </div>
            <div className="space-y-0.5 border-t border-white/[0.06] pt-1.5">
              {m.targets.map((tp, i) => <Tp key={i} n={i + 1} tp={tp} st={m.tpStatus[i]} d={d} />)}
            </div>
            <div className="text-[8.5px] font-mono text-zinc-600" data-testid="pme-scale-in-rule">
              <span className="text-fuchsia-300/80">SCALE-IN {m.scaleInRule?.rule === "R_FLOW" ? "RULE" : "SCORE"} </span>
              {m.scaleInRule?.checks ? Object.entries(m.scaleInRule.checks).map(([k, ok]) => (
                <span key={k} className={`mr-1.5 ${ok ? "text-emerald-400" : "text-zinc-500"}`} data-testid={`pme-scale-check-${k}`}>{ok ? "✓" : "○"} {k === "r" ? `+${m.scaleInRule.min_r}R` : k === "be" ? "BE" : k === "htf" ? "4H" : k === "flow" ? "FLOW" : k === "adds" ? `ADDS<${m.scaleInRule.max_adds}` : k.toUpperCase()}</span>
              )) : <><SmoothNum value={m.scaleInScore} format={(n) => String(Math.round(n))} />/100</>}
              {m.scaleInBlock ? <span className="text-zinc-500">· {m.scaleInBlock}</span> : <span className="text-fuchsia-300">· ELIGIBLE → adding 1 lot, stop to stack break-even</span>}
              {m.sizeNote ? <div className="text-amber-400/90 mt-0.5">{m.sizeNote}</div> : null}
            </div>
            {(pmRec || rs) && (
              <div className="rounded border border-white/[0.06] px-2 py-1.5 space-y-0.5 text-[8.5px] font-mono" data-testid="pme-agents">
                {pmRec && <div className="flex items-center gap-1.5"><Layers size={9} className="text-cyan-300" /><span className="text-cyan-300 font-bold">{pmRec.action}</span><span className="text-zinc-500">{Math.round(pmRec.confidence || 0)}% · {(pmRec.reasoning || [])[0] || ""}</span></div>}
                {rs && <div className="flex items-center gap-1.5"><Shield size={9} className="text-amber-300" /><span className={`font-bold ${rs.verdict === "ALLOW" ? "text-emerald-300" : "text-amber-300"}`}>RISK {rs.verdict}</span><span className="text-zinc-500">{(rs.reasoning || [])[0] || ""}</span></div>}
              </div>
            )}
            {!!(m.actions || []).length && (
              <div className="text-[8.5px] font-mono text-zinc-500">
                last: {m.actions[m.actions.length - 1].type} {m.actions[m.actions.length - 1].price ?? m.actions[m.actions.length - 1].qty ?? ""} {m.actions[m.actions.length - 1].authorized ? "· EXECUTED" : "· shadow"}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
